import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useEffect } from 'react';

// Layout Components
export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f9fafb;
  font-family: 'Inter', sans-serif;
`;

export const NavBar = styled.div`
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
`;

export const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.disabled ? '#CBD5E0' : '#4B5563'};
  background: none;
  border: none;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;

  &:hover {
    color: ${props => props.disabled ? '#CBD5E0' : '#1F2937'};
    background-color: ${props => props.disabled ? 'transparent' : '#F3F4F6'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  svg {
    width: 20px;
    height: 20px;
    color: #4B5563;
  }
  
  span {
    font-weight: 500;
    color: #1F2937;
  }
`;

export const MainContent = styled.div`
  flex-grow: 1;
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

export const ContentContainer = styled.div`
  max-width: 48rem;
  margin: 0 auto;
  width: 100%;
`;

export const SectionTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1.5rem;
`;

export const ProgressBarContainer = styled.div`
  background-color: white;
  border-top: 1px solid #E5E7EB;
  padding: 1rem;
`;

export const ProgressBarWrapper = styled.div`
  max-width: 48rem;
  margin: 0 auto;
`;

export const ProgressBarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  
  & > span:first-child {
    font-size: 0.875rem;
    color: #6B7280;
  }
  
  & > span:last-child {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }
`;

export const ProgressBarTrack = styled.div`
  width: 100%;
  height: 0.5rem;
  background-color: #E5E7EB;
  border-radius: 9999px;
  overflow: hidden;
`;

export const ProgressBarIndicator = styled.div`
  height: 100%;
  background-color: #8B5CF6;
  border-radius: 9999px;
  transition: width 0.3s ease;
`;

// Heart Animation Components
export const HeartIconContainer = styled.div`
  width: 6rem;
  height: 6rem;
  border-radius: 9999px;
  background-color: #FEE2E2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  align-self: center;
`;

export const AnimatedHeart = styled(motion.div)`
  color: #EF4444;
  transform-origin: center;
`;

// Instruction Components
export const WelcomeText = styled.p`
  color: #7E22CE;
  font-weight: 500;
  margin-bottom: 1rem;
  font-size: 1.125rem;
  text-align: center;
`;

export const InstructionCard = styled.div`
  width: 100%;
  background-color: #F5F3FF;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #DDD6FE;
  margin-bottom: 1rem;
  
  & > p {
    color: #4B5563;
    margin-bottom: 1rem;
  }
`;

export const ActionButton = styled.button`
  width: 100%;
  margin-top: 1rem;
  background-color: #8B5CF6;
  color: white;
  font-weight: 600;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #7C3AED;
  }
  
  &:focus {
    outline: 2px solid #A78BFA;
  }
`;

// Audio Input Component
export const VoiceInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
`;

export const MicButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 0.375rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #F3F4F6;
  }
`;

interface MicIconProps {
  $isListening?: boolean;
}

export const MicIcon = styled.div<MicIconProps>`
  color: ${props => props.$isListening ? '#EF4444' : '#8B5CF6'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
  }

  ${props => props.$isListening && `
    animation: pulse 1.5s infinite;
    @keyframes pulse {
      0% { opacity: 1; }
      50% { opacity: 0.5; }
      100% { opacity: 1; }
    }
  `}
`;

export const VoiceInputButton = styled.button`
  flex: 1;
  border: 1px solid #DDD6FE;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  text-align: left;
  color: #6B7280;
  background-color: white;
  transition: all 0.2s;
  font-size: 0.95rem;
  
  &:hover {
    background-color: #F5F3FF;
    border-color: #C4B5FD;
  }
`;

// Response Display
export const ResponseCard = styled.div`
  margin-top: 1rem;
  background-color: #F0FDF4;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #BBFFC9;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

export const ResponseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const CheckIconWrapper = styled.div`
  background-color: #DCFCE7;
  border-radius: 9999px;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 16px;
    height: 16px;
    color: #047857;
  }
`;

export const ResponseContent = styled.div`
  flex: 1;
  
  p:first-child {
    color: #374151;
    margin-bottom: 0.25rem;
  }
  
  p:last-child {
    color: #047857;
    font-size: 0.875rem;
  }
`;

// Heart Rate Components
export const HeartRateContainer = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  border: 1px solid #E5E7EB;
  margin-top: 1.5rem;
`;

export const HeartRateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  & > span:first-child {
    color: #6B7280;
    font-size: 0.875rem;
  }
  
  & > span:last-child {
    color: #111827;
    font-weight: 600;
    font-size: 1.125rem;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const ExerciseButton = styled.button`
  flex: 1;
  background-color: #8B5CF6;
  color: white;
  font-weight: 600;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #7C3AED;
  }
`;

export const RestButton = styled.button`
  flex: 1;
  background-color: #F3F4F6;
  color: #4B5563;
  font-weight: 600;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #E5E7EB;
  }
`;

// 3D Heart Model Container
export const HeartModelContainer = styled.div`
  margin-top: 2rem;
  width: 100%;
  height: 90vh;
  min-height: 800px;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  position: relative;
  
  @media (max-width: 768px) {
    height: 70vh;
    min-height: 500px;
  }
`;

export const ModelContainer = styled.div`
  width: 676;
  height: 676;
  position: relative;
  margin: 0 auto;
  background: '#000';
  border-radius: '12px';
  overflow: 'hidden';
`;

// Heart Lesson Components
export const LessonContainer = styled.div`
  padding: 2rem;
  background-color: #f9fafb;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

export const QuestionText = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
`;

export const AnswerButton = styled.button`
  background-color: #8B5CF6;
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #7C3AED;
  }
`;

export const FeedbackText = styled.p`
  margin-top: 1rem;
  font-size: 1rem;
  color: #047857;
`;

export const HintButton = styled.button`
  display: flex;
  align-items: center;
  color: #8B5CF6;
  font-size: 0.875rem;
  background: none;
  border: none;
  padding: 0.5rem 0;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #7C3AED;
  }
  
  svg {
    margin-right: 0.25rem;
  }
`;

export const HintContainer = styled.div`
  background-color: #FEF3C7;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #FCD34D;
  margin-top: 1rem;
  
  p {
    color: #4B5563;
  }
`;

export const useVoiceResponse = () => {
  useEffect(() => {
    const handleVoiceResponse = (event: Event) => {
      // Remove unused customEvent variable
      // const customEvent = event as CustomEvent<VoiceLearningResponse>;
      // Handle the response here
    };

    window.addEventListener('voiceLearningResponse', handleVoiceResponse);
    return () => window.removeEventListener('voiceLearningResponse', handleVoiceResponse);
  }, []);
}; 